import React, {useEffect, useRef, useState} from 'react'
import styled, {useTheme} from 'styled-components'
import theme from '../theme'
import {Box, Link as CoreLink, Button as CoreButton, useWindowSize} from '@miro-site/core'
import SecondaryNavigation from './SecondaryNavigation'
import {IHeader} from '../api/interfaces'
import Highlighted from './Highlighted'
import CustomerSpotlight from './CustomerSpotlight'
import TemplatesHighilighted from './TemplatesHighlighted'
import {Transition} from 'react-transition-group'
import {HeaderAppearance} from '../../types/Header'

const appearanceColorMap: Record<HeaderAppearance, Record<string, string>> = {
  dark: {
    buttonColor: theme.header.topNavigation.whiteColor,
    buttonExpandedColor: theme.colors.outspoken,
    linkColor: theme.header.topNavigation.whiteColor,
    linkHoverColor: theme.colors.outspoken,
    linkIndicatorBackground: theme.colors.outspoken,
  },
  light: {
    buttonColor: theme.header.topNavigation.color,
    buttonExpandedColor: theme.header.topNavigation.hoverColor,
    linkColor: theme.header.topNavigation.color,
    linkHoverColor: theme.header.topNavigation.hoverColor,
    linkIndicatorBackground: theme.colors.primaryBlueHover,
  },
}

const Link = styled(CoreLink).attrs<{$appearance: HeaderAppearance}>(({$appearance, theme}) => ({
  $styles: {
    color: appearanceColorMap[$appearance].linkColor,
    ':hover': {
      color: appearanceColorMap[$appearance].linkHoverColor,
    },
  },
}))<{$appearance: HeaderAppearance}>`
  display: block;
  white-space: nowrap;

  ${({theme}) =>
    theme.normalizeStyles({
      transition: `color ${theme.animation.transitionDuration}`,
      fontSize: theme.header.topNavigation.fontSize,
      lineHeight: theme.header.topNavigation.lineHeight,

      ':focus': {
        ...theme.focus.outlines.withOffset,
      },
    })}
`

type TopNavigationButton = React.ComponentProps<typeof CoreButton> & {
  $isExpanded?: boolean
  $appearance: HeaderAppearance
}

const getChevronBackground = (color: string) =>
  `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${encodeURIComponent(
    color,
  )}' stroke-width='1.5'/%3E%3C/svg%3E%0A")`

const Button = styled(CoreButton).attrs<TopNavigationButton>(props => ({
  'data-top-navigation-button': 1,
  'aria-haspopup': true,
  'aria-expanded': !!props.$isExpanded,
}))<TopNavigationButton>`
  ${({theme, $isExpanded, $appearance}) =>
    theme.normalizeStyles({
      transition: `color ${theme.animation.transitionDuration}`,
      fontSize: theme.header.topNavigation.fontSize,
      lineHeight: theme.header.topNavigation.lineHeight,
      color: $isExpanded
        ? appearanceColorMap[$appearance].buttonExpandedColor
        : appearanceColorMap[$appearance].buttonColor,
      ':hover': {
        color: appearanceColorMap[$appearance].buttonExpandedColor,
        '.focus-trap:after': {
          backgroundImage: getChevronBackground(
            appearanceColorMap[$appearance].buttonExpandedColor,
          ),
        },
      },
      ':focus': {
        ...theme.focus.outlines.withOffset,
      },
      '.focus-trap:after': {
        transition: theme.header.topNavigation.animation.chevron,
        transform: $isExpanded ? 'rotate(-180deg)' : 'rotate(0)',
        display: 'inline-block',
        marginLeft: 8,
        content: "''",
        width: 10,
        height: 10,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: getChevronBackground(
          $isExpanded
            ? appearanceColorMap[$appearance].buttonExpandedColor
            : appearanceColorMap[$appearance].buttonColor,
        ),
        position: 'relative',
        top: [null, 2, 2, 2, null],
      },
    })}
`

const List = styled(Box).attrs(({theme}) => ({
  as: 'ul',
  role: 'menu',
  $styles: {
    display: 'flex',
    alignItems: 'center',
  },
}))`
  ${Link}, ${Button} {
    display: block;
  }
`

List.displayName = 'TopNavigation.List'

const Container = styled(Box).attrs(({theme}) => ({
  $styles: {
    display: ['none', 'none', 'flex'],
    marginLeft: theme.header.topNavigation.leftOffset,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
}))``

Container.displayName = 'TopNavigation.Container'

type LinkIndicatorProps = {
  activeNavigation: number | null
  activeItemRef: React.RefObject<HTMLLIElement | null>
  appearance: HeaderAppearance
}

const LinkIndicator = (props: LinkIndicatorProps): React.ReactElement => {
  const {activeNavigation, activeItemRef, appearance} = props

  const [indicatorPosition, setIndicatorPosition] = useState<[number, number]>([0, 0])

  const theme = useTheme()

  const {width} = useWindowSize()

  const [indicatorOffset, indicatorWidth] = indicatorPosition

  useEffect(() => {
    if (activeItemRef.current) {
      const item = activeItemRef.current

      const itemOffset = item.offsetLeft
      const itemRect = item.getBoundingClientRect()

      const itemComputedStyle = window.getComputedStyle(item, null)

      const paddingLeft = parseFloat(itemComputedStyle.paddingLeft)
      const paddingRight = parseFloat(itemComputedStyle.paddingRight)

      const itemWidth = itemRect.width - paddingLeft - paddingRight

      setIndicatorPosition([itemOffset + paddingLeft, itemWidth])
    } else {
      setIndicatorPosition(prev => [prev[0], 0])
    }
  }, [activeNavigation, width])

  const states = {
    entering: {
      transition: 'none 0s ease 0s',
      transform: `translateX(${indicatorOffset}px) scaleX(0)`,
    },
  }

  return (
    <Transition in={activeNavigation !== null} timeout={0}>
      {state => (
        <Box
          $styles={{
            transformOrigin: 'left',
            transition: theme.header.topNavigation.animation.linkIndicator,
            transform: `translateX(${indicatorOffset}px) scaleX(${indicatorWidth / 100})`,
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100px',
            height: 2,
            background: appearanceColorMap[appearance].linkIndicatorBackground,
            ...(states[state] || {}),
          }}
        />
      )}
    </Transition>
  )
}

LinkIndicator.displayName = 'LinkIndicator'

const Item = styled.li.attrs({
  role: 'menuitem',
})<{$isExpanded?: boolean}>`
  ${props =>
    props.theme.normalizeStyles({
      paddingLeft: props.theme.header.topNavigation.gutter,
      paddingRight: props.theme.header.topNavigation.gutter,
      ':first-child': {
        paddingLeft: 0,
      },
      ':last-child': {
        paddingRight: 0,
      },
    })}
`

Item.displayName = 'TopNavigation.Item'

type TopNavigationProps = {
  appearance: HeaderAppearance
  isFixed?: boolean
  activeNavigation: number | null
  activeNavigationRef?: React.MutableRefObject<HTMLElement | null>
  onToggleNavigation: (activeNavigation: number) => void
} & Pick<
  IHeader,
  | 'productNavigation'
  | 'whatsNew'
  | 'solutionsNavigation'
  | 'templates'
  | 'resourcesNavigation'
  | 'customerSpotlight'
  | 'enterpriseLink'
  | 'pricingLink'
>

const TopNavigation = (props: TopNavigationProps): React.ReactElement => {
  const {
    activeNavigation,
    activeNavigationRef,
    onToggleNavigation,
    productNavigation,
    whatsNew,
    solutionsNavigation,
    templates,
    resourcesNavigation,
    customerSpotlight,
    enterpriseLink,
    pricingLink,
    appearance,
  } = props

  const prevNavigation = useRef<number | null>(null)

  const activeItemRef = useRef<HTMLLIElement | null>(null)

  useEffect(() => {
    prevNavigation.current = activeNavigation
  }, [activeNavigation])

  const setActiveNavigationRef = (navigationIndex: number, ref: HTMLElement | null): void => {
    if (!activeNavigationRef) {
      return
    }

    if (activeNavigation === null) {
      activeNavigationRef.current = null
    }

    if (navigationIndex === activeNavigation) {
      activeNavigationRef.current = ref
    }
  }

  const setactiveItemRef = (navigationIndex: number, ref: HTMLLIElement | null): void => {
    if (activeNavigation === null) {
      activeItemRef.current = null
    }

    if (navigationIndex === activeNavigation) {
      activeItemRef.current = ref
    }
  }

  return (
    <Container>
      <List>
        <Item ref={ref => setactiveItemRef(0, ref)} $isExpanded={activeNavigation === 0}>
          <Button
            data-track-id="top-navigation-button"
            aria-controls={'product-navigation'}
            $isExpanded={activeNavigation === 0}
            $appearance={appearance}
            onClick={() => onToggleNavigation(0)}
          >
            {productNavigation.title}
          </Button>
          <SecondaryNavigation
            id={'product-navigation'}
            ref={ref => setActiveNavigationRef(0, ref)}
            index={0}
            prevNavigation={prevNavigation.current}
            nextNavigation={activeNavigation}
            navigationGroups={productNavigation.groups}
            highlightedContent={<Highlighted {...whatsNew} />}
          />
        </Item>
        <Item ref={ref => setactiveItemRef(1, ref)} $isExpanded={activeNavigation === 1}>
          <Button
            data-track-id="top-navigation-button"
            aria-controls={'solutions-navigation'}
            $isExpanded={activeNavigation === 1}
            $appearance={appearance}
            onClick={() => onToggleNavigation(1)}
          >
            {solutionsNavigation.title}
          </Button>
          <SecondaryNavigation
            id={'solutions-navigation'}
            ref={ref => setActiveNavigationRef(1, ref)}
            index={1}
            prevNavigation={prevNavigation.current}
            nextNavigation={activeNavigation}
            navigationGroups={solutionsNavigation.groups}
            highlightedContent={<TemplatesHighilighted {...templates} />}
          />
        </Item>
        <Item ref={ref => setactiveItemRef(2, ref)} $isExpanded={activeNavigation === 2}>
          <Button
            data-track-id="top-navigation-button"
            aria-controls={'resources-navigation'}
            $isExpanded={activeNavigation === 2}
            $appearance={appearance}
            onClick={() => onToggleNavigation(2)}
          >
            {resourcesNavigation.title}
          </Button>
          <SecondaryNavigation
            id={'resources-navigation'}
            ref={ref => setActiveNavigationRef(2, ref)}
            index={2}
            prevNavigation={prevNavigation.current}
            nextNavigation={activeNavigation}
            navigationGroups={resourcesNavigation.groups}
            highlightedContent={<CustomerSpotlight {...customerSpotlight} />}
          />
        </Item>
        <Item>
          <Link href={enterpriseLink.path} $appearance={appearance}>
            {enterpriseLink.title}
          </Link>
        </Item>
        <Item>
          <Link href={pricingLink.path} $appearance={appearance}>
            {pricingLink.title}
          </Link>
        </Item>
      </List>
      <LinkIndicator
        activeNavigation={activeNavigation}
        activeItemRef={activeItemRef}
        appearance={appearance}
      />
    </Container>
  )
}

export default Object.assign(TopNavigation, {Link: Link, Button: Button, Item: Item})
